import axios from 'axios';

// API configuration — resolved at runtime (not build time) so mobile/LAN/ngrok hosts
// can hit the right backend.
//
// Local development default: hit **production** (`api.blocknotify.com`) through the
// `/api` dev-server proxy (see `craco.config.js`). This lets us test the full
// signed-in experience locally without running a local backend.
// Override with `REACT_APP_API_URL=http://localhost:4000/v1` (or any URL) if you
// want to target a local/staging backend instead.
const getApiBaseUrl = () => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    const isDev = process.env.NODE_ENV === 'development';
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const envUrl = process.env.REACT_APP_API_URL;

    // Explicit override wins — but for prod URLs on localhost in dev, still go
    // through the `/api` proxy to avoid CORS.
    if (envUrl) {
        if (isDev && isLocalhost && envUrl.includes('api.blocknotify.com')) {
            return '/api';
        }
        return envUrl;
    }

    // Production build — direct to prod API.
    if (!isDev) {
        return 'https://api.blocknotify.com/v1';
    }

    // Dev on localhost with no env override → proxy to prod by default.
    if (isLocalhost) {
        return '/api';
    }

    // ngrok: map `-app.*` frontend host to matching `-api.*` backend host.
    if (hostname.includes('ngrok')) {
        if (hostname.includes('blocknotify-app')) {
            return 'https://blocknotify-api.ngrok-free.app/v1';
        }
        const backendHost = hostname.replace('-app.', '-api.');
        return `https://${backendHost}/v1`;
    }

    // Dev via LAN IP (phone on same network) — assume a local backend on :4000.
    return `http://${hostname}:4000/v1`;
};

// Create axios instance with dynamic baseURL
const api = axios.create({
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true, // Enable sending cookies and auth headers
});

// Add request interceptor to set baseURL dynamically for each request
api.interceptors.request.use(
    (config) => {
        // Set baseURL dynamically on each request
        const baseURL = getApiBaseUrl();
        config.baseURL = baseURL;

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// --- Refresh token support ---
let isRefreshing = false;
let refreshPromise = null; // eslint-disable-line no-unused-vars
const refreshSubscribers = [];
let isRedirecting = false; // Prevent multiple redirects
let lastRedirectTime = 0;
const REDIRECT_DEBOUNCE_MS = 5000; // Don't redirect more than once per 5 seconds

const subscribeTokenRefresh = (cb) => {
    refreshSubscribers.push(cb);
};

const onRefreshed = (newAccessToken) => {
    refreshSubscribers.forEach((cb) => cb(newAccessToken));
    refreshSubscribers.length = 0;
};

// Soft redirect — dispatches an event for AuthContext to handle via React Router,
// avoiding a hard page reload that destroys all app state.
const redirectToLogin = () => {
    const now = Date.now();
    if (isRedirecting || (now - lastRedirectTime) < REDIRECT_DEBOUNCE_MS) {
        return;
    }
    isRedirecting = true;
    lastRedirectTime = now;
    
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    
    if (window.location.pathname !== '/login') {
        console.log('🔄 Auth expired — triggering soft logout');
        window.dispatchEvent(new CustomEvent('auth:forceLogout'));
    }
    
    setTimeout(() => {
        isRedirecting = false;
    }, REDIRECT_DEBOUNCE_MS);
};

// Request interceptor for auth
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const status = error.response?.status;
        const originalRequest = error.config || {};

        // Retry on 502/503 (transient backend/service startup / LB errors)
        if (status === 502 || status === 503) {
            const retryCount = originalRequest._503RetryCount || 0;
            const MAX_503_RETRIES = 5;

            if (retryCount < MAX_503_RETRIES) {
                originalRequest._503RetryCount = retryCount + 1;

                const retryAfterHeader = error.response?.headers?.['retry-after'];
                // Jittered exponential backoff: 2s, 4s, 8s, 16s, 32s (capped at 32s)
                const baseBackoff = retryAfterHeader
                    ? parseInt(retryAfterHeader, 10) * 1000
                    : Math.min(2000 * Math.pow(2, retryCount), 32000);
                const jitter = Math.random() * 1000; // 0–1s random jitter
                const backoffMs = baseBackoff + jitter;

                console.debug(
                    `⏳ ${status} — retry ${retryCount + 1}/${MAX_503_RETRIES} in ${Math.round(backoffMs)}ms (${originalRequest.url})`
                );

                await new Promise((resolve) => setTimeout(resolve, backoffMs));
                return api.request(originalRequest);
            }

            console.warn(`❌ ${status} — all ${MAX_503_RETRIES} retries exhausted (${originalRequest.url})`);
        }

        if (status === 401) {
            const url = originalRequest.url || '';
            // Endpoints that should NOT trigger refresh logic; UI handles these 401s.
            // CRITICAL: refresh-tokens must be here. Without it, a 401 from the
            // refresh call itself re-entered this interceptor, saw isRefreshing
            // = true, and parked itself in the wait-for-refresh queue — which
            // only wakes when the refresh promise settles, i.e. never. Every
            // pending request then hung forever: dead sessions showed an
            // infinite "Loading…" dashboard instead of redirecting to login.
            const authHandledRegex = /\/auth\/(login|register|refresh-tokens|logout|verify-email|forgot-password|reset-password)/i;
            if (authHandledRegex.test(url)) {
                return Promise.reject(error);
            }

            // Avoid infinite loop
            if (originalRequest._retry) {
                // Hard failure → clear and redirect (debounced)
                redirectToLogin();
                return Promise.reject(error);
            }

            const storedRefreshToken = localStorage.getItem('refreshToken');
            if (!storedRefreshToken) {
                // No refresh token → redirect (debounced)
                redirectToLogin();
                return Promise.reject(error);
            }

            originalRequest._retry = true;

            if (!isRefreshing) {
                isRefreshing = true;
                refreshPromise = api.post('/auth/refresh-tokens', { refreshToken: storedRefreshToken })
                    .then((res) => {
                        const { access, refresh } = res.data || {};
                        if (access?.token) {
                            localStorage.setItem('accessToken', access.token);
                            api.defaults.headers.Authorization = `Bearer ${access.token}`;
                            if (typeof window !== 'undefined') {
                                window.dispatchEvent(
                                    new CustomEvent('auth:tokenRefreshed', {
                                        detail: { accessToken: access.token },
                                    })
                                );
                            }
                        }
                        if (refresh?.token) {
                            localStorage.setItem('refreshToken', refresh.token);
                            if (typeof window !== 'undefined') {
                                window.dispatchEvent(
                                    new CustomEvent('auth:refreshTokenUpdated', {
                                        detail: { refreshToken: refresh.token },
                                    })
                                );
                            }
                        }
                        onRefreshed(access?.token || '');
                        return access?.token;
                    })
                    .catch((refreshErr) => {
                        console.error('🔴 Token refresh failed:', refreshErr.message);
                        // Wake up subscribers so they don't hang forever
                        onRefreshed('');
                        // Use debounced redirect
                        redirectToLogin();
                        throw refreshErr;
                    })
                    .finally(() => {
                        isRefreshing = false;
                        refreshPromise = null;
                    });
            }

            return new Promise((resolve, reject) => {
                subscribeTokenRefresh((newAccessToken) => {
                    if (newAccessToken) {
                        originalRequest.headers = originalRequest.headers || {};
                        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                    }
                    api.request(originalRequest).then(resolve).catch(reject);
                });
            });
        }
        return Promise.reject(error);
    }
);

// Address API endpoints
export const addressAPI = {
    // Get all addresses
    getAddresses: async (params = {}) => {
        const response = await api.get('/addresses', { params });
        return response.data;
    },

    // Get all watched addresses (simplified)
    getAllWatchedAddresses: async (forceRefresh = false) => {
        // Add cache-busting parameter to force fresh data
        const timestamp = Date.now();
        const url = forceRefresh
            ? `/addresses/all?t=${timestamp}&refresh=true`
            : `/addresses/all?t=${timestamp}`;
        const response = await api.get(url);
        return response.data;
    },

    // Create new address
    createAddress: async (addressData) => {
        const response = await api.post('/addresses', addressData);
        return response.data;
    },

    // Get single address with basic data
    getAddress: async (address) => {
        const response = await api.get(`/addresses/${address}`);
        return response.data;
    },

    // Get comprehensive address data (balances, transactions, approvals)
    getAddressComprehensiveData: async (address, refreshOptions = null) => {
        let url = `/addresses/${address}/comprehensive-data`;

        // Add refresh options as query parameter if provided
        if (refreshOptions) {
            const refreshParam = typeof refreshOptions === 'string'
                ? refreshOptions
                : JSON.stringify(refreshOptions);
            url += `?refresh=${encodeURIComponent(refreshParam)}`;
        }

        const response = await api.get(url);
        return response.data;
    },

    // On-demand approval scanning (user-triggered from the Approvals page)
    startApprovalScan: async (address) => {
        const response = await api.post(`/addresses/${address}/approvals/scan`);
        return response.data;
    },
    getApprovalScanStatus: async (address) => {
        const response = await api.get(`/addresses/${address}/approvals/scan-status`);
        return response.data;
    },

    // Get address balances from backend (fast, cached) - simplified
    getAddressBalances: async (address, refresh = false) => {
        // Use comprehensive data endpoint for all balance data
        const refreshOptions = refresh ? { balances: true } : null;
        return await addressAPI.getAddressComprehensiveData(address, refreshOptions);
    },

    // Update address
    updateAddress: async (address, updateData) => {
        const response = await api.patch(`/addresses/${address}`, updateData);
        return response.data;
    },

    // Persist per-address activity filter settings
    updateAddressEventFilter: async (address, eventFilter) => {
        const response = await api.patch(`/addresses/${address}`, { eventFilter });
        return response.data;
    },

    // Delete address
    deleteAddress: async (address) => {
        const response = await api.delete(`/addresses/${address}`);
        return response.data;
    },

    // Remove address (alias for deleteAddress)
    removeAddress: async (address) => {
        const response = await api.delete(`/addresses/${address}`);
        return response.data;
    },
};

// Alert API endpoints
export const alertAPI = {
    // Get all alerts
    getAlerts: async (params = {}) => {
        const response = await api.get('/alerts', { params });
        return response.data;
    },

    // Create new alert
    createAlert: async (alertData) => {
        const response = await api.post('/alerts', alertData);
        return response.data;
    },

    // Get single alert
    getAlert: async (alertId) => {
        const response = await api.get(`/alerts/${alertId}`);
        return response.data;
    },

    // Update alert
    updateAlert: async (alertId, updateData) => {
        const response = await api.patch(`/alerts/${alertId}`, updateData);
        return response.data;
    },

    // Toggle a single trigger's enabled flag
    toggleTrigger: async (alertId, triggerId, enabled) => {
        const response = await api.patch(`/alerts/${alertId}`, {
            triggerId,
            enabledTrigger: !!enabled,
        });
        return response.data;
    },

    // Toggle multiple triggers in one call
    toggleTriggers: async (alertId, triggerUpdates) => {
        // triggerUpdates: Array<{ _id: string, enabled: boolean }>
        const response = await api.patch(`/alerts/${alertId}`, {
            triggers: (triggerUpdates || []).map(t => ({ triggerId: t.triggerId || t._id, enabled: !!t.enabled })),
        });
        return response.data;
    },

    // Delete alert
    deleteAlert: async (alertId) => {
        const response = await api.delete(`/alerts/${alertId}`);
        return response.data;
    },

    // Bulk operations — the API has no bulk routes; fan out per alert.
    bulkDeleteAlerts: async (alertIds) => {
        const results = await Promise.allSettled((alertIds || []).map((id) => api.delete(`/alerts/${id}`)));
        const failed = results.filter((r) => r.status === 'rejected').length;
        return { deleted: results.length - failed, failed };
    },

    bulkToggleAlerts: async (alertIds, enabled) => {
        const results = await Promise.allSettled(
            (alertIds || []).map((id) => api.patch(`/alerts/${id}`, { enabled: !!enabled }))
        );
        const failed = results.filter((r) => r.status === 'rejected').length;
        return { updated: results.length - failed, failed };
    },

    // Manual alert checking
    checkAllAlerts: async () => {
        const response = await api.post('/alerts/check-all');
        return response.data;
    },

    checkAlert: async (alertId) => {
        const response = await api.post(`/alerts/${alertId}/check`);
        return response.data;
    },

    checkAlertsForAddress: async (address) => {
        const response = await api.post(`/alerts/check-address/${address}`);
        return response.data;
    },

    // Manual alert triggering (for testing)
    triggerAlert: async (alertId) => {
        const response = await api.post(`/alerts/${alertId}/trigger`);
        return response.data;
    },

    // Sent alerts history
    getSentAlerts: async (params = {}) => {
        const response = await api.get('/alerts/history', { params });
        return response.data;
    },

    // Get sent alerts for a specific address
    getSentAlertsForAddress: async (address, limit = 100) => {
        const response = await api.get('/alerts/history', { 
            params: { address: address.toLowerCase(), limit } 
        });
        return response.data;
    },

    // Mark sent alert as read
    markSentAlertRead: async (sentAlertId) => {
        const response = await api.patch(`/alerts/history/${sentAlertId}/read`);
        return response.data;
    },

    // Get unread alert count
    getUnreadAlertCount: async () => {
        const response = await api.get('/alerts/history/unread-count');
        return response.data;
    },
};

export const healthAPI = {
    // Check API health
    checkHealth: async () => {
        const response = await api.get('/health');
        return response.data;
    },
};

// Auth API endpoints
export const authAPI = {

    // Login
    login: async (credentials) => {
        const response = await api.post('/auth/login', credentials);
        return response.data;
    },

    // Register
    register: async (userData) => {
        const response = await api.post('/auth/register', userData);
        return response.data;
    },

    // Logout
    logout: async (data) => {
        const response = await api.post('/auth/logout', data);
        return response.data;
    },

    // Refresh token
    refreshToken: async (refreshToken) => {
        const response = await api.post('/auth/refresh-tokens', { refreshToken });
        return response.data;
    },

    // Forgot password
    forgotPassword: async (email) => {
        const response = await api.post('/auth/forgot-password', { email });
        return response.data;
    },

    // Reset password
    resetPassword: async (token, password) => {
        const response = await api.post('/auth/reset-password', { token, password });
        return response.data;
    },

    // Verify email
    verifyEmail: async (token) => {
        const response = await api.post(`/auth/verify-email?token=${encodeURIComponent(token)}`);
        return response.data;
    },

    // Helper method for direct API access
    post: async (endpoint, data) => {
        const response = await api.post(endpoint, data);
        return response;
    },
};

// User API endpoints
export const userAPI = {
    // Single-call startup payload: user + watched addresses + recent tx.
    getBootstrap: async () => {
        const response = await api.get('/users/bootstrap');
        return response.data;
    },

    // Get user profile
    getProfile: async () => {
        const response = await api.get('/users/profile');
        return response.data;
    },

    // Update user profile
    updateProfile: async (userData) => {
        const response = await api.patch('/users/profile', userData);
        return response.data;
    },

    // Delete the signed-in user's own account (DELETE /users/me)
    deleteMe: async () => {
        const response = await api.delete('/users/me');
        return response.data;
    },
};

// Slack API functions
export const slackAPI = {
    getOAuthUrl: async () => {
        const response = await api.get('/slack/oauth/url');
        return response.data;
    },

    getWorkspace: async () => {
        const response = await api.get('/slack/workspace');
        return response.data;
    },

    disconnectWorkspace: async () => {
        const response = await api.delete('/slack/workspace');
        return response.data;
    },

    getChannels: async (workspaceId) => {
        const response = await api.get(`/slack/channels?workspace_id=${workspaceId}`);
        return response.data;
    },

    testConnection: async (workspaceId, channel) => {
        const response = await api.post('/slack/test', {
            workspace_id: workspaceId,
            channel: channel
        });
        return response.data;
    },

    // New endpoints for connection management
    getConnectionStatus: async () => {
        const response = await api.get('/slack/connection-status');
        return response.data;
    },

};

// Webhook API functions
export const webhookAPI = {
    // Get all webhook configurations (cache-bust to get fresh data)
    getWebhooks: async () => {
        const response = await api.get(`/users/webhooks?_t=${Date.now()}`);
        return response.data;
    },

    // Update a webhook (type: 'slack', 'discord', or 'custom')
    updateWebhook: async (type, data) => {
        const response = await api.patch(`/users/webhooks/${type}`, data);
        return response.data;
    },

    // Test a webhook
    testWebhook: async (type) => {
        const response = await api.post(`/users/webhooks/${type}/test`);
        return response.data;
    },

    // Delete/remove a webhook
    deleteWebhook: async (type) => {
        const response = await api.delete(`/users/webhooks/${type}`);
        return response.data;
    },

};

export const newsAPI = {
    // Get paginated news articles
    getNews: async (params = {}) => {
        const { limit = 20, offset = 0, source, category } = params;
        const queryParams = new URLSearchParams({ limit, offset });
        if (source) queryParams.set('source', source);
        if (category) queryParams.set('category', category);
        const response = await api.get(`/news?${queryParams}`);
        return response.data;
    },

    // Get news feed for landing page
    getNewsFeed: async (limit = 10) => {
        const response = await api.get(`/news/feed?limit=${limit}`);
        return response.data;
    },

    // Get available sources
    getSources: async () => {
        const response = await api.get('/news/sources');
        return response.data;
    },

};

export const telegramAPI = {
    // Get connect deep link
    connect: async () => {
        const response = await api.post('/telegram/connect');
        return response.data;
    },

    // Disconnect telegram
    disconnect: async () => {
        const response = await api.delete('/telegram/disconnect');
        return response.data;
    },

    // Get connection status
    getStatus: async () => {
        const response = await api.get('/telegram/status');
        return response.data;
    },

    // Send test message
    sendTest: async () => {
        const response = await api.post('/telegram/test');
        return response.data;
    },
};

// Export the base URL function for components that need to make direct fetch calls
export { getApiBaseUrl };

export default api; 
